import { FieldType } from "@certa/types";
import {
  getNanoId,
  type Process as ProcessV3,
  type ProcessV1,
  type ProcessV2
} from "@certa/studio-core";
import { integrationFieldTypeData } from "@certa/studio/src/constants/field.constants";

const getCWFTableFields = (process: ProcessV1) => {
  const fields = Object.values(process.fields);
  let cwfTableFieldsHavingOldFormatOfChildMappings: ProcessV1["fields"][""][] =
    [];
  if (fields?.length) {
    cwfTableFieldsHavingOldFormatOfChildMappings = fields.filter(field => {
      return (
        field.fieldType === FieldType.TABLE &&
        field?.extra?.l0 &&
        field?.extra?.child_mapping
      );
    });
  }
  return cwfTableFieldsHavingOldFormatOfChildMappings;
};

export const migrateCWFTableChildMappings = (process: ProcessV1): ProcessV1 => {
  const filteredFields = getCWFTableFields(process);
  if (!filteredFields?.length || !process) {
    return structuredClone(process);
  }

  let aggregatedChildMappings = {};

  const updatedFields = filteredFields.map(cwfTableField => {
    const reverseParsedField = { ...cwfTableField };
    const childMapping = reverseParsedField.extra.child_mapping;
    const newChildMappingTag =
      reverseParsedField.extra.l0.datasource.child_mapping_field_tag ??
      `${reverseParsedField.tag}_childMapping_${getNanoId()}`;

    reverseParsedField.extra.l0.datasource.child_mapping_tag =
      newChildMappingTag;
    aggregatedChildMappings = {
      ...aggregatedChildMappings,
      [newChildMappingTag]: {
        child_field_mapping: childMapping.child_field_mapping,
        child_workflow_kind: reverseParsedField?.extra?.l0?.datasource?.kind_tag
      }
    };

    // Delete old mappings
    delete reverseParsedField.extra.l0.child_mapping_field_tag;
    delete reverseParsedField.extra.child_mapping;
    reverseParsedField.__modifiedAt = Date.now();
    return reverseParsedField;
  });

  // Note: Not wrapping this in a transaction because
  // the parent action (migrateProcess) is already wrapped in a transaction
  const updatedProcess = structuredClone(process);

  updatedFields.forEach(field => {
    updatedProcess.fields[field.id] = field;
  });

  updatedProcess.process.childMappings = {
    ...updatedProcess.process.childMappings,
    ...aggregatedChildMappings
  };
  updatedProcess.process.__modifiedAt = Date.now();
  return updatedProcess;
};

export const migrateToNewECAProcess = (
  process: ProcessV1 | ProcessV2
): ProcessV2 => {
  const stateRules = ("stateRules" in process ? process.stateRules : {}) ?? {};
  const eventRules = ("eventRules" in process ? process.eventRules : {}) ?? {};

  const updatedProcess: ProcessV2 = structuredClone({
    ...process,
    stateRules,
    eventRules,
    process: {
      ...process.process,
      stateRuleSequence: [],
      eventRuleSequence: []
    }
  });

  // loop over stateRules and eventRules and add them to the updatedProcess
  Object.values(stateRules).forEach(stateRule => {
    updatedProcess.process.stateRuleSequence.push(stateRule.id);
  });
  Object.values(eventRules).forEach(eventRule => {
    updatedProcess.process.eventRuleSequence.push(eventRule.id);
  });
  updatedProcess.process.__modifiedAt = Date.now();
  return updatedProcess;
};

// Common utility to reverse migrate a single integration field
const reverseMigrateIntegrationField = (
  field: ProcessV1["fields"][""]
): ProcessV1["fields"][""] => {
  const reverseMigratedField = structuredClone(field);

  // Only reverse migrate if field has fieldType = "integration" and extra.api_type that exists in integrationFieldTypeData
  if (
    field.fieldType === "integration" &&
    field.extra?.api_type &&
    Object.keys(integrationFieldTypeData).includes(field.extra.api_type)
  ) {
    // Restore the original fieldType from extra.api_type
    reverseMigratedField.fieldType = field.extra.api_type;

    // Remove api_type from extra
    if (reverseMigratedField.extra) {
      delete reverseMigratedField.extra.api_type;

      // Clean up extra object if it's empty
      if (Object.keys(reverseMigratedField.extra).length === 0) {
        delete reverseMigratedField.extra;
      }
    }
    reverseMigratedField.__modifiedAt = Date.now();
  }

  return reverseMigratedField;
};

export const reverseMigrateIntegrationFieldsFromNewFormat = (
  process: ProcessV1
) => {
  const fields = Object.values(process.fields);
  if (!fields?.length) return structuredClone(process);

  const updatedFields = fields.map(reverseMigrateIntegrationField);

  const updatedProcess = structuredClone(process);

  updatedFields.forEach(field => {
    updatedProcess.fields[field.id] = field;
  });
  return updatedProcess;
};

export const migrateToAppJSON = (processV2: ProcessV2): ProcessV3 => {
  const oldProcess = structuredClone(processV2);
  const oldProcessProps = oldProcess.process;

  const {
    availableStatuses,
    defaultStatus,
    name,
    tag,
    staticGroupFeatures,
    dynamicGroupFeatures,
    staticGroupsOptional,
    objectTypes,
    dynamicGroups,
    dynamicGroupPermissions,
    runIntegrationsOnArchive,
    runIntegrationsOnCreate,
    originalName,
    ...rest
  } = oldProcessProps;
  const processProps: ProcessV3["process"] = {
    ...rest,
    kind: {
      availableStatuses,
      defaultStatus,
      name,
      tag,
      permissions: {
        staticGroups: staticGroupFeatures,
        dynamicGroups: dynamicGroupFeatures
      },
      visibleToStaticGroups: staticGroupsOptional,
      objectTypes
    },
    dynamicGroupNames: dynamicGroups,
    dynamicGroupNamesWithPerm: dynamicGroupPermissions,
    onDeleteIntegrationFieldTags: runIntegrationsOnArchive,
    autoIntegrationCallFieldTags: runIntegrationsOnCreate,
    __modifiedAt: Date.now()
  };

  const oldSwimlanes = Object.values(oldProcess.swimlanes);
  const swimlaneMap: ProcessV3["swimlanes"] = {};
  oldSwimlanes.forEach(swimlane => {
    const { dynamicGroups, ...rest } = swimlane;
    swimlaneMap[swimlane.id] = {
      ...rest,
      dynamicGroupNames: dynamicGroups,
      __modifiedAt: Date.now()
    };
  });

  const oldSteps = Object.values(oldProcess.steps);
  const stepMap: ProcessV3["steps"] = {};
  oldSteps.forEach(step => {
    const {
      deadline,
      dynamicGroups,
      showStepLogsOnParent: shouldShowStepLogsOnParent,
      taggableDynamicGroups,
      ...rest
    } = step;
    stepMap[step.id] = {
      ...rest,
      availableUserTags: rest.availableUserTags === "yes" ? ["Assignee"] : [],
      deadline: deadline?.format === "days" ? deadline?.deadline : undefined,
      deadlineMins:
        deadline?.format === "hours" ? deadline?.deadline : undefined,
      dynamicGroupNames: dynamicGroups,
      visibleCWActivityLog: shouldShowStepLogsOnParent,
      taggableDynamicGroupNames: taggableDynamicGroups,
      __modifiedAt: Date.now()
    };
  });

  const oldFields = Object.values(oldProcess.fields);
  const fieldMap: ProcessV3["fields"] = {};
  oldFields.forEach(field => {
    const {
      height,
      csvSource,
      csvFilterColumns,
      integrationButtonText,
      showStepLogsOnParent: shouldShowStepLogsOnParent,
      dataJSONJmesPath,
      dataJSONPath,
      ...rest
    } = field;
    fieldMap[field.id] = {
      ...rest,
      jmesPath: dataJSONJmesPath,
      dataJSONPath: dataJSONPath?.split(","),
      sourceCSVJSON:
        csvSource || csvFilterColumns
          ? {
              csvSource: csvSource,
              filterColumns: csvFilterColumns
            }
          : undefined,
      meta: height
        ? {
            height
          }
        : undefined,
      visibleCWActivityLog: shouldShowStepLogsOnParent,
      __modifiedAt: Date.now()
    };
  });

  const oldEventRules = Object.values(oldProcess.eventRules ?? {});
  const eventRuleMap: ProcessV3["eventRules"] = {};
  oldEventRules.forEach(eventRule => {
    const { id, ...rest } = eventRule;
    eventRuleMap[id] = {
      ...rest,
      __studioId: id,
      rules: rest.rules.map(rule => ({
        ...rule,
        actions: rule.actions.map(action => ({
          ...action,
          __studioId: getNanoId()
        })),
        __studioId: getNanoId()
      })),
      events: rest.events.map(event => ({
        ...event,
        __studioId: getNanoId()
      })),
      __modifiedAt: Date.now()
    };
  });

  const oldStateRules = Object.values(oldProcess.stateRules ?? {});
  const stateRuleMap: ProcessV3["stateRules"] = {};
  oldStateRules.forEach(stateRule => {
    const { id, ...rest } = stateRule;
    stateRuleMap[stateRule.id] = {
      ...rest,
      __studioId: id,
      __modifiedAt: Date.now()
    };
  });

  return {
    ...oldProcess,
    process: processProps,
    eventRules: eventRuleMap,
    stateRules: stateRuleMap,
    swimlanes: swimlaneMap,
    steps: stepMap,
    fields: fieldMap
  };
};
